import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { GlgLoadingSpinner } from 'SharedComponents'
import { toggleSort } from '../store/filtersSlice'
import { useGetRosterQuery } from '../store/rosterApi'
import RosterTableRow from './RosterTableRow'

const t = (key) => (!window.I18n ? key : window.I18n.t(key))

// Declared widths (100% with phone) so headers do not jump on re-sort.
const COLUMNS_BEFORE_PHONE = [
  { key: 'name',        width: '12%', labelKey: 'customer_groups.groups.members.columns.handle' },
  { key: 'first_name',  width: '10%', labelKey: 'customer_groups.groups.members.columns.first_name' },
  { key: 'last_name',   width: '10%', labelKey: 'customer_groups.groups.members.columns.last_name' },
  { key: 'gender',      width: '7%',  labelKey: 'customer_groups.groups.members.columns.gender' },
  { key: 'email',       width: '16%', labelKey: 'customer_groups.groups.members.columns.email' },
]

// Labelled with the club's handicap-id term when it has one.
const GHIN_COLUMN = { key: 'ghin', width: '11%', labelKey: 'customer_groups.groups.members.columns.ghin' }

const PHONE_COLUMN = { key: 'phone', width: '10%' }

const COLUMNS_AFTER_PHONE = [
  { key: 'usga_index',  width: '11%', labelKey: 'customer_groups.groups.members.columns.handicap_index' },
  { key: 'status',      width: '9%', labelKey: 'customer_groups.groups.members.columns.status' },
]

const ACTIONS_COLUMN_WIDTH = '4%'

// Decorative: the sort state is announced from aria-sort on the header cell.
const SortIcons = ({ columnKey, currentColumn, currentOrder }) => {
  const isActive = currentColumn === columnKey
  return (
    <span className='on_hover_icons' aria-hidden='true'>
      <i className={'gga-carret-up' + (isActive && currentOrder === 'asc' ? ' active' : '')} />
      <i className={'gga-carret-down' + (isActive && currentOrder === 'desc' ? ' active' : '')} />
    </span>
  )
}

const ariaSortFor = (key, filters) => {
  if (filters.column !== key) return 'none'

  return filters.sortOrder === 'desc' ? 'descending' : 'ascending'
}

SortIcons.propTypes = {
  columnKey: PropTypes.string.isRequired,
  currentColumn: PropTypes.string.isRequired,
  currentOrder: PropTypes.string.isRequired,
}

const RosterTable = ({ onEditMember }) => {
  const dispatch = useDispatch()
  const filters  = useSelector((state) => state.filters)
  const phoneFieldName = useSelector((state) => state.misc.data.phoneFieldName)
  const handicapProvider = useSelector((state) => state.misc.data.handicapProvider)
  const { data, isLoading, isError } = useGetRosterQuery(filters)

  if (isLoading) {
    return (
      <div className='text-center add-margin-top-20'>
        <GlgLoadingSpinner />
      </div>
    )
  }

  if (isError) {
    return (
      <div className='alert alert-danger'>
        {t('customer_groups.groups.members.load_error')}
      </div>
    )
  }

  const members = data?.members ?? []

  // One definition drives the colgroup, headers and colSpan.
  const columns = [
    ...COLUMNS_BEFORE_PHONE,
    { ...GHIN_COLUMN, label: handicapProvider || t(GHIN_COLUMN.labelKey) },
    ...(phoneFieldName ? [ { ...PHONE_COLUMN, label: phoneFieldName } ] : []),
    ...COLUMNS_AFTER_PHONE,
  ]

  return (
    <div className='table-responsive btsp_table_container'>
      <table className='table table-striped table-hover btsp_table customer-groups-roster-table'>
        <colgroup>
          {columns.map(({ key, width }) => <col key={key} style={{ width }} />)}
          <col style={{ width: ACTIONS_COLUMN_WIDTH }} />
        </colgroup>
        <thead>
          <tr className='head'>
            {columns.map(({ key, label, labelKey }) => (
              <th key={key} scope='col' aria-sort={ariaSortFor(key, filters)}>
                <button type='button' className='roster-sort-header' onClick={() => dispatch(toggleSort(key))}>
                  <span>{label || t(labelKey)}</span>
                  <SortIcons columnKey={key} currentColumn={filters.column} currentOrder={filters.sortOrder} />
                </button>
              </th>
            ))}
            <th scope='col'><span className='sr-only'>{t('customer_groups.groups.members.columns.actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className='text-center text-muted'>
                {t('customer_groups.groups.members.no_members')}
              </td>
            </tr>
          ) : (
            members.map((member) => (
              <RosterTableRow key={member.id} member={member} onEditMember={onEditMember} phoneFieldName={phoneFieldName} />
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

RosterTable.propTypes = {
  onEditMember: PropTypes.func.isRequired,
}

export default RosterTable
